import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import type { PreviewUrlSuggestion } from '@dashboard/services/previewUrlService'
import { fetchPreviewUrlSuggestions } from '@dashboard/services/previewUrlService'
import { openDebuggerSession } from '@dashboard/services/debuggerSession'
import { formatPreviewUrl, normalizePreviewUrl } from '@dashboard/utils/previewUrl'

export interface PreviewUrlEntry extends PreviewUrlSuggestion {
  source: 'suggested' | 'custom'
}

interface StoredPreviewUrls {
  version: number
  lastUrl: string | null
  customUrls: string[]
  suggestions: PreviewUrlSuggestion[]
  fetchedAt: number
}

const STORAGE_VERSION = 1
const CACHE_TTL_MS = 10 * 60 * 1000
const MAX_HITLIST_URLS = 5
const MAX_CUSTOM_URLS = 10
const LEGACY_STORAGE_KEY = 'pastPreviewUrls'
const memoryStore = new Map<string, StoredPreviewUrls>()

function getStorageKey(teamPid: string | number): string {
  return `rm_preview_urls_${teamPid}`
}

function hasWindow(): boolean {
  return typeof window !== 'undefined'
}

function readStorage(key: string): string | null {
  if (!hasWindow()) {
    return null
  }

  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  if (!hasWindow()) {
    return
  }

  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Ignore unavailable storage and keep the in-memory fallback only.
  }
}

function emptyState(): StoredPreviewUrls {
  return {
    version: STORAGE_VERSION,
    lastUrl: null,
    customUrls: [],
    suggestions: [],
    fetchedAt: 0
  }
}

function sanitizeSuggestion(raw: PreviewUrlSuggestion): PreviewUrlSuggestion | null {
  const normalizedUrl = normalizePreviewUrl(raw.url)
  if (!normalizedUrl) {
    return null
  }

  const sources = Array.from(
    new Set(raw.sources.filter((source) => source === 'flow_executions' || source === 'campaign_exposures'))
  )
  if (sources.length === 0) {
    return null
  }

  return {
    url: normalizedUrl,
    uniqueVisitors: Number.isFinite(raw.uniqueVisitors) ? Math.max(0, Math.round(raw.uniqueVisitors)) : 0,
    events: Number.isFinite(raw.events) ? Math.max(0, Math.round(raw.events)) : 0,
    sources
  }
}

function sanitizeSuggestions(raw: PreviewUrlSuggestion[]): PreviewUrlSuggestion[] {
  const merged = new Map<string, PreviewUrlSuggestion>()

  for (const suggestion of raw) {
    const sanitized = sanitizeSuggestion(suggestion)
    if (!sanitized) {
      continue
    }

    const existing = merged.get(sanitized.url)
    if (!existing) {
      merged.set(sanitized.url, sanitized)
      continue
    }

    merged.set(sanitized.url, {
      url: sanitized.url,
      uniqueVisitors: existing.uniqueVisitors + sanitized.uniqueVisitors,
      events: existing.events + sanitized.events,
      sources: Array.from(
        new Set([...existing.sources, ...sanitized.sources])
      ).sort() as PreviewUrlSuggestion['sources']
    })
  }

  return Array.from(merged.values())
    .sort((left, right) => {
      if (left.uniqueVisitors !== right.uniqueVisitors) {
        return right.uniqueVisitors - left.uniqueVisitors
      }

      if (left.events !== right.events) {
        return right.events - left.events
      }

      return left.url.localeCompare(right.url)
    })
    .slice(0, MAX_HITLIST_URLS)
}

function normalizeCustomUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return []
  }

  const deduped: string[] = []
  const seen = new Set<string>()

  for (const candidate of raw) {
    if (typeof candidate !== 'string') {
      continue
    }

    const normalized = normalizePreviewUrl(candidate)
    if (!normalized || seen.has(normalized)) {
      continue
    }

    seen.add(normalized)
    deduped.push(normalized)

    if (deduped.length >= MAX_CUSTOM_URLS) {
      break
    }
  }

  return deduped
}

function sanitizeStoredState(raw: unknown): StoredPreviewUrls {
  if (!raw || typeof raw !== 'object') {
    return emptyState()
  }

  const state = raw as Partial<StoredPreviewUrls>
  const customUrls = normalizeCustomUrls(state.customUrls)
  const normalizedLastUrl = typeof state.lastUrl === 'string' ? normalizePreviewUrl(state.lastUrl) : null
  const lastUrl = normalizedLastUrl && customUrls.includes(normalizedLastUrl) ? normalizedLastUrl : normalizedLastUrl
  const suggestions = sanitizeSuggestions(Array.isArray(state.suggestions) ? state.suggestions : [])
  const fetchedAt = typeof state.fetchedAt === 'number' && Number.isFinite(state.fetchedAt) ? state.fetchedAt : 0

  return {
    version: STORAGE_VERSION,
    lastUrl,
    customUrls,
    suggestions,
    fetchedAt
  }
}

function readLegacyUrls(): string[] {
  const raw = readStorage(LEGACY_STORAGE_KEY)
  if (!raw) {
    return []
  }

  try {
    return normalizeCustomUrls(JSON.parse(raw))
  } catch {
    return []
  }
}

function persistStoredState(key: string, state: StoredPreviewUrls): void {
  memoryStore.set(key, state)
  writeStorage(key, JSON.stringify(state))
}

function readStoredState(teamPid: string | number): StoredPreviewUrls {
  const key = getStorageKey(teamPid)
  const raw = readStorage(key)

  if (raw) {
    try {
      const state = sanitizeStoredState(JSON.parse(raw))
      memoryStore.set(key, state)
      return state
    } catch {
      const state = emptyState()
      memoryStore.set(key, state)
      return state
    }
  }

  const fallback = memoryStore.get(key)
  if (fallback) {
    return fallback
  }

  const legacyUrls = readLegacyUrls()
  if (legacyUrls.length === 0) {
    const state = emptyState()
    memoryStore.set(key, state)
    return state
  }

  const migratedState: StoredPreviewUrls = {
    version: STORAGE_VERSION,
    lastUrl: legacyUrls[0] ?? null,
    customUrls: legacyUrls.slice(0, MAX_CUSTOM_URLS),
    suggestions: [],
    fetchedAt: 0
  }

  persistStoredState(key, migratedState)
  // Intentionally keep the legacy key: it is global (not per-team), so other
  // teams still need it to migrate their own copy on first hydrate.

  return migratedState
}

function getEntryForUrl(
  url: string,
  customUrlSet: Set<string>,
  suggestionMap: Map<string, PreviewUrlSuggestion>
): PreviewUrlEntry {
  if (customUrlSet.has(url)) {
    return {
      url,
      uniqueVisitors: 0,
      events: 0,
      sources: [],
      source: 'custom'
    }
  }

  const suggestion = suggestionMap.get(url)
  if (suggestion) {
    return {
      ...suggestion,
      source: 'suggested'
    }
  }

  return {
    url,
    uniqueVisitors: 0,
    events: 0,
    sources: [],
    source: 'custom'
  }
}

export function usePreviewUrls(options: { teamPid: Ref<string | number | 'demo' | null>; isDemoMode: Ref<boolean> }) {
  const customUrls = ref<string[]>([])
  const suggestions = ref<PreviewUrlSuggestion[]>([])
  const lastUrl = ref<string | null>(null)
  const fetchedAt = ref(0)
  const isLoading = ref(false)
  let currentKey: string | null = null
  let inFlight: Promise<PreviewUrlSuggestion[]> | null = null

  const persistState = () => {
    if (!currentKey) {
      return
    }

    persistStoredState(currentKey, {
      version: STORAGE_VERSION,
      lastUrl: lastUrl.value,
      customUrls: [...customUrls.value],
      suggestions: [...suggestions.value],
      fetchedAt: fetchedAt.value
    })
  }

  const hydrateState = (teamPid: string | number | 'demo' | null) => {
    if (!teamPid || teamPid === 'demo') {
      currentKey = null
      customUrls.value = []
      suggestions.value = []
      lastUrl.value = null
      fetchedAt.value = 0
      return
    }

    currentKey = getStorageKey(teamPid)
    const state = readStoredState(teamPid)
    customUrls.value = state.customUrls
    suggestions.value = state.suggestions
    lastUrl.value = state.lastUrl
    fetchedAt.value = state.fetchedAt
  }

  watch(
    options.teamPid,
    (teamPid) => {
      hydrateState(teamPid)
    },
    { immediate: true }
  )

  const hitlist = computed<PreviewUrlEntry[]>(() => {
    const orderedUrls = [lastUrl.value, ...customUrls.value, ...suggestions.value.map((entry) => entry.url)]

    const deduped: PreviewUrlEntry[] = []
    const seen = new Set<string>()
    const customUrlSet = new Set(customUrls.value)
    const suggestionMap = new Map(suggestions.value.map((entry) => [entry.url, entry]))

    for (const candidate of orderedUrls) {
      if (!candidate || seen.has(candidate)) {
        continue
      }

      seen.add(candidate)
      deduped.push(getEntryForUrl(candidate, customUrlSet, suggestionMap))

      if (deduped.length >= MAX_HITLIST_URLS) {
        break
      }
    }

    return deduped
  })

  const activeUrl = computed(() => hitlist.value[0]?.url ?? null)

  const loadSuggestions = async () => {
    const teamPid = options.teamPid.value
    if (options.isDemoMode.value || !teamPid || teamPid === 'demo') {
      return []
    }

    const isFresh = suggestions.value.length > 0 && Date.now() - fetchedAt.value < CACHE_TTL_MS
    if (isFresh) {
      return suggestions.value
    }

    if (inFlight) {
      return inFlight
    }

    isLoading.value = true
    inFlight = fetchPreviewUrlSuggestions(teamPid)
      .then((result) => {
        const sanitized = sanitizeSuggestions(result)
        suggestions.value = sanitized
        fetchedAt.value = sanitized.length > 0 ? Date.now() : 0
        persistState()
        return sanitized
      })
      .catch(
        () =>
          // Keep any previously cached suggestions on a transient fetch error
          // (5xx / network blip) rather than wiping the user's hitlist out of
          // localStorage. Leave fetchedAt untouched so the existing cache stays
          // valid and a later call can retry.
          suggestions.value
      )
      .finally(() => {
        isLoading.value = false
        inFlight = null
      })

    return inFlight
  }

  const openPreviewUrl = async (url: string, source: 'suggested' | 'custom') => {
    const normalizedUrl = normalizePreviewUrl(url)
    const formattedUrl = formatPreviewUrl(url)
    const teamPid = options.teamPid.value
    if (!normalizedUrl || !formattedUrl) {
      return
    }

    lastUrl.value = normalizedUrl

    if (source === 'custom') {
      customUrls.value = [normalizedUrl, ...customUrls.value.filter((entry) => entry !== normalizedUrl)].slice(
        0,
        MAX_CUSTOM_URLS
      )
    }

    persistState()
    void recordAnalyticsEvent(AnalyticsEventTypes.PREVIEW_STARTED, { url: normalizedUrl, source })
    if (options.isDemoMode.value || teamPid === 'demo') {
      window.open(formattedUrl, '_blank')
      return
    }

    if (!teamPid) {
      return
    }

    await openDebuggerSession(teamPid, formattedUrl)
  }

  const promptForCustomUrl = async () => {
    const input = window.prompt('Enter a URL to preview on your site')
    if (input === null) {
      return
    }

    const normalizedUrl = normalizePreviewUrl(input)
    if (!normalizedUrl) {
      window.alert('Enter a valid http(s) URL.')
      return
    }

    await openPreviewUrl(normalizedUrl, 'custom')
  }

  const openActivePreview = async () => {
    if (activeUrl.value) {
      const entry = hitlist.value.find((item) => item.url === activeUrl.value)
      await openPreviewUrl(activeUrl.value, entry?.source ?? 'suggested')
      return
    }

    await promptForCustomUrl()
  }

  const removeCustomUrl = (url: string) => {
    const normalizedUrl = normalizePreviewUrl(url)
    if (!normalizedUrl) {
      return
    }

    customUrls.value = customUrls.value.filter((entry) => entry !== normalizedUrl)
    if (lastUrl.value === normalizedUrl) {
      lastUrl.value = null
    }

    persistState()
  }

  return {
    hitlist,
    customUrls,
    activeUrl,
    isLoading,
    loadSuggestions,
    openPreviewUrl,
    openActivePreview,
    promptForCustomUrl,
    removeCustomUrl
  }
}
